import { AreaChart, Area, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import type { DailyActivity } from '@lens/types/claude'
import { parseISO, subDays } from 'date-fns'
import { makeDateTickFormatter, formatDateFull } from '@lens/lib/charts'
import { ChartGrid, ChartTooltipBox, ChartXAxis, ChartYAxis } from '../chart-primitives'

interface Props {
 data: DailyActivity[]
 days?: number
 dateFrom?: string
 dateTo?: string
 /** ResponsiveContainer height — a px number, or "100%" to fill a sized parent. */
 height?: number | `${number}%`
}

interface TooltipPayload {
 active?: boolean
 payload?: Array<{ name: string; value: number; color: string }>
 label?: string
}

function UsageTooltip({ active, payload, label }: TooltipPayload) {
 if (!active || !payload?.length) return null
 return (
  <ChartTooltipBox>
   <p className="text-foreground mb-1">{formatDateFull(String(label))}</p>
   {payload.map((p) => (
    <p key={p.name} style={{ color: p.color }}>
     {p.name}: <span className="tabular-nums">{p.value.toLocaleString()}</span>
    </p>
   ))}
  </ChartTooltipBox>
 )
}

function toIsoDate(val: string): string | null {
 const m = val.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/)
 if (m) {
  const [, month, day, year] = m
  return `${year}-${month!.padStart(2, '0')}-${day!.padStart(2, '0')}`
 }
 const iso = val.match(/^(\d{4})-(\d{2})-(\d{2})/)
 return iso ? val.slice(0, 10) : null
}

function parseDateInput(val: string): Date | null {
 const parsed = new Date(val)
 return isNaN(parsed.getTime()) ? null : parsed
}

export function UsageOverTimeChart({ data, days = 90, dateFrom, dateTo, height = 220 }: Props) {
 let filtered: { date: string; messages: number; sessions: number }[]

 if (dateFrom && dateTo) {
  const fromStr = toIsoDate(dateFrom.trim())
  const toStr = toIsoDate(dateTo.trim())
  if (fromStr && toStr && fromStr <= toStr) {
   filtered = data
    .filter((d) => {
     const dataDate = /^\d{4}-\d{2}-\d{2}/.test(d.date)
      ? d.date.slice(0, 10)
      : toIsoDate(d.date)
     if (!dataDate) return false
     return dataDate >= fromStr && dataDate <= toStr
    })
    .map((d) => ({
     date: d.date.slice(0, 10),
     messages: d.messageCount,
     sessions: d.sessionCount,
    }))
  } else {
   filtered = []
  }
 } else {
  const cutoff = subDays(new Date(), days)
  filtered = data
   .filter((d) => parseISO(d.date) >= cutoff)
   .map((d) => ({
    date: d.date.slice(0, 10),
    messages: d.messageCount,
    sessions: d.sessionCount,
   }))
 }

 if (filtered.length === 0) {
  const from = dateFrom && dateTo ? parseDateInput(dateFrom) : null
  const to = dateFrom && dateTo ? parseDateInput(dateTo) : null
  const isFuture = from && to && from > new Date()
  return (
   <div className="flex flex-col items-center justify-center h-full min-h-48 text-muted-foreground type-body gap-1">
    <span>no data</span>
    {isFuture && (
     <span className="type-body">(selected range is in the future)</span>
    )}
    {dateFrom && dateTo && !isFuture && (
     <span className="type-body">(no activity in this date range)</span>
    )}
   </div>
  )
 }

 const dateFormatter = makeDateTickFormatter(filtered.map((d) => d.date))

 return (
  <ResponsiveContainer width="100%" height={height}>
   <AreaChart data={filtered} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
    <defs>
     <linearGradient id="gradMessages" x1="0" y1="0" x2="0" y2="1">
      <stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.3} />
      <stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0} />
     </linearGradient>
     <linearGradient id="gradSessions" x1="0" y1="0" x2="0" y2="1">
      <stop offset="5%" stopColor="var(--chart-2)" stopOpacity={0.2} />
      <stop offset="95%" stopColor="var(--chart-2)" stopOpacity={0} />
     </linearGradient>
    </defs>
    <ChartGrid vertical={false} />
    <ChartXAxis
     dataKey="date"
     tickFormatter={dateFormatter}
     interval="preserveStartEnd"
     minTickGap={50}
    />
    <ChartYAxis
     tickFormatter={(v: number) =>
      v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toLocaleString()
     }
    />
    <Tooltip content={<UsageTooltip />} />
    <Legend
     wrapperStyle={{ paddingTop: "calc(var(--spacing) * 2)" }}
     formatter={(value) => <span>{value}</span>}
    />
    <Area
     type="monotone"
     dataKey="messages"
     stroke="var(--chart-1)"
     strokeWidth={2}
     fill="url(#gradMessages)"
     dot={false}
     activeDot={{ r: 3, fill: 'var(--chart-1)' }}
     isAnimationActive={false}
    />
    <Area
     type="monotone"
     dataKey="sessions"
     stroke="var(--chart-2)"
     strokeWidth={1.5}
     fill="url(#gradSessions)"
     dot={false}
     activeDot={{ r: 3, fill: 'var(--chart-2)' }}
     isAnimationActive={false}
    />
   </AreaChart>
  </ResponsiveContainer>
 )
}
